import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useLocation } from "wouter"
import { api, ApiRequestError } from "@/lib/api"
import { useAdminAuthStore } from "@/store/adminAuthStore"
import { useEffect } from "react"

interface AdminUser {
    userId: string
    role: string
    email: string
    needsReconsent?: {
        terms: boolean
        privacy: boolean
        currentTermsVersion: string
        currentPrivacyVersion: string
    }
}

export const useAdminAuth = () => {
    const { user, setUser, logout: clearUser } = useAdminAuthStore()
    const [, navigate] = useLocation()
    const queryClient = useQueryClient()

    const { isLoading } = useQuery({
        queryKey: ["admin", "me"],
        queryFn: async () => {
            const data = await api.get<AdminUser>("/auth/me")
            setUser(data)
            return data
        },
        retry: false,
        staleTime: 5 * 60 * 1000,
    })

    const loginMutation = useMutation({
        mutationFn: (creds: { email: string; password: string }) =>
            api.post<AdminUser>("/auth/login", creds),
        onSuccess: async (data) => {
            // Login returns the bare identity; needsReconsent only comes
            // from /auth/me. Seed the cache with what we have, then
            // immediately refetch /auth/me so the modal-trigger flag is
            // populated before AdminGuard renders.
            setUser(data)
            queryClient.setQueryData(["admin", "me"], data)
            await queryClient.invalidateQueries({ queryKey: ["admin", "me"] })
            navigate("~/admin")
        },
    })

    const logoutMutation = useMutation({
        mutationFn: () => api.post<{ message: string }>("/auth/logout", {}),
        onSuccess: () => {
            clearUser()
            queryClient.clear()
            navigate("~/admin/login")
        },
    })

    return {
        user,
        isLoading,
        login: loginMutation.mutateAsync,
        loginError: loginMutation.error instanceof ApiRequestError ? loginMutation.error.message : null,
        isLoggingIn: loginMutation.isPending,
        logout: logoutMutation.mutate,
    }
}
